import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AuthenticationSelectors,
  ConfigService,
  requestTenantsContext,
} from '@orion-platform/core';
import { environment } from '@orion/environment';
import { Helper } from 'apps/orion/src/app/helper/helper';
import { Observable } from 'rxjs';

import {
  Carriers,
  CommitHistoryDto,
  CommitHistoryNameValueDto,
  CommitModuleRedirectDTO,
  Countries,
  DocumentNameDto,
  FileData,
  MergeCommit,
  Reason,
  RequestStatusDto,
  TransportType,
  VendorName,
} from '../models/commit.model';
import {
  ApiResponse,
  BatchEditCommits,
  Commit,
  CommitsFilterDownloadDTO,
  CommitsFilterDto,
  CommitsFilterUIModel,
} from '../models/supply-visibility.model';
import { FormSetting } from '../store/carriers-list/carriers-list.types';
import { SetDateRangeParameters } from '../store/supply-visibility/supply-visibility.actions';
import { COMMIT_STATUS_OPTIONS } from '../store/supply-visibility/supply-visibility.consts';
import { CommitStatusOption } from '../store/supply-visibility/supply-visibility.types';
import {
  applyDateFilter,
  buildBaseCommitDto,
  vendorsToUpper,
} from '../store/supply-visibility/supply-visibility.utils';
import { generateHttpParams, getUrl } from '../utils/general.utils';
import { splitWith } from '../utils/string.utils';
import { DefaultFormSettingsService } from './default-form-settings.service';
import { FileUploadService } from './file-upload.service';

@Injectable({
  providedIn: 'root',
})
export class CommitsService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly config = inject(ConfigService);
  private readonly fileUpload = inject(FileUploadService);
  private readonly defaultForms = inject(DefaultFormSettingsService);

  private readonly mm = environment.modulesBaseUrl.materialManagement;
  private readonly baseUrl = this.config.getSettings('apiBaseUrl') as string;

  private get commitsUrl(): string {
    const suffix =
      this.store.selectSnapshot(AuthenticationSelectors.activePlant) === 'MULTI' ? 'Orch' : '';
    return getUrl(this.baseUrl, `${this.mm.commits}${suffix}`);
  }
  private readonly dummyCommitsUrl = getUrl(this.baseUrl, this.mm.dummyCommits);

  filteredData: CommitModuleRedirectDTO | null = null;
  private vendorWithName: VendorName[] = [];

  setVendorWithNameList(v: VendorName[]): void {
    this.vendorWithName = v ?? [];
  }

  getVendorWithNameList(): VendorName[] {
    return this.vendorWithName;
  }

  private commitsAction(action: string): string {
    return `${this.commitsUrl}/${action}`;
  }

  private commitDocumentsAction(inboundDeliveryNumber: string, action: string): string {
    return `${this.commitsUrl}/${inboundDeliveryNumber}/${action}`;
  }

  private upnSnapshot(): string {
    return this.store.selectSnapshot(AuthenticationSelectors.upn) as string;
  }

  getCommitStatusList(): CommitStatusOption[] {
    return [...COMMIT_STATUS_OPTIONS];
  }

  getCommitsByFilter(
    filter: Record<string, unknown> & { plants?: string[] },
  ): Observable<Commit[]> {
    const { plants, ...payload } = filter;
    return this.http.post<Commit[]>(this.commitsAction('GetCommitsByFilter'), payload, {
      context: requestTenantsContext(plants),
    });
  }

  getCommits(filter: Array<{ key: string; value: string }>): Observable<Commit[]> {
    const params = generateHttpParams(
      Object.fromEntries(filter.map((f): [string, string] => [f.key, f.value])),
    );
    return this.http.get<Commit[]>(this.commitsAction('GetCommits'), { params });
  }

  editCommit(record: Commit): Observable<Commit> {
    const payload = Helper.formatCommitDate(record);
    return this.http.post<Commit>(this.commitsAction('EditCommit'), payload);
  }

  batchEditCommit(filter: BatchEditCommits): Observable<RequestStatusDto> {
    const payload = Helper.formatBatchCommitDate(filter);
    return this.http.post<RequestStatusDto>(this.commitsAction('BatchEditCommit'), payload);
  }

  batchEditCommitRequestStatus(requestHash: string): Observable<RequestStatusDto> {
    return this.http.get<RequestStatusDto>(this.commitsAction(`-/RequestStatus/${requestHash}`));
  }

  deleteCommit(
    commit: Commit & { WithPevaConfirm?: boolean },
    lowerTriggerUrl = '',
  ): Observable<Commit> {
    const c = Helper.formatCommitDate(commit);

    const url = `${this.commitsAction('DeleteCommit')}?InboundDeliveryNumber=${c.inboundDeliveryNumber}${lowerTriggerUrl}`;

    return this.http.post<Commit>(url, {
      inboundDeliveryNumber: c.inboundDeliveryNumber,
      parentID: c['parentID'] && c['parentID'] !== '' ? c['parentID'] : undefined,
      withMergeModifyQuantity: c['withMergeModifyQuantity'] || undefined,
      ...(c['WithPevaConfirm'] ? { WithPevaConfirm: true } : {}),
    });
  }

  manualSplitCommit(commits: Commit[]): Observable<Commit[]> {
    return this.http.post<Commit[]>(this.commitsAction('ManualSplitCommits'), commits);
  }

  manualMergeCommit(commits: MergeCommit): Observable<Commit> {
    return this.http.post<Commit>(this.commitsAction('ManualMergeCommits'), commits);
  }

  getCommitHistory(inboundDeliveryNumber: string): Observable<CommitHistoryDto[]> {
    const params = new HttpParams().set('InboundDeliveryNumber', inboundDeliveryNumber);
    return this.http.get<CommitHistoryDto[]>(this.commitsAction('GetCommitHistory'), { params });
  }

  getCommitHistoryTable(inboundDeliveryNumber: string): Observable<CommitHistoryNameValueDto[][]> {
    const params = new HttpParams().set('inboundDeliveryNumber', inboundDeliveryNumber);
    return this.http.get<CommitHistoryNameValueDto[][]>(
      this.commitsAction('GetCommitHistoryTable'),
      {
        params,
      },
    );
  }

  getDummyCommitDetailHistory(dummyCommitDetailId: string): Observable<CommitHistoryDto[]> {
    return this.http.get<CommitHistoryDto[]>(
      `${this.dummyCommitsUrl}/GetDummyCommitDetailHistory`,
      { params: generateHttpParams({ dummyCommitDetailId }) },
    );
  }

  getDummyCommitDetailHistoryTable(
    dummyCommitDetailId: string,
  ): Observable<CommitHistoryNameValueDto[][]> {
    return this.http.get<CommitHistoryNameValueDto[][]>(
      `${this.dummyCommitsUrl}/GetDummyCommitDetailHistoryTable`,
      { params: generateHttpParams({ dummyCommitDetailId }) },
    );
  }

  getReasonsList(): Observable<Reason[]> {
    return this.http.get<Reason[]>(this.commitsAction('GetReasons'));
  }

  getMandatoryDates(): Observable<string[]> {
    return this.http.get<string[]>(this.commitsAction('GetMandatoryDates'));
  }

  getCommitsCarriers(): Observable<ApiResponse<Carriers[]>> {
    const isMulti = this.store.selectSnapshot(AuthenticationSelectors.activePlant) === 'MULTI';
    const path = isMulti ? `${this.mm.carriers}Orch` : this.mm.carriers;
    return this.http.get<ApiResponse<Carriers[]>>(getUrl(this.baseUrl, path));
  }

  getCommitsTransportType(): Observable<TransportType[]> {
    return this.http.get<TransportType[]>(getUrl(this.baseUrl, this.mm.transportType));
  }

  getCommitsCountries(): Observable<ApiResponse<Countries[]>> {
    return this.http.get<ApiResponse<Countries[]>>(getUrl(this.baseUrl, this.mm.countries));
  }

  getReadOnlyFields(): Observable<string[]> {
    return this.http.get<string[]>(getUrl(this.baseUrl, this.mm.commitsReadOnlyFields));
  }

  /** Under MULTI, vendors are always loaded for all available plants (not the plant filter). */
  getVendorsWithNames(onlyForBuy = false): Observable<ApiResponse<VendorName[]> | VendorName[]> {
    const params = new HttpParams().set('OnlyForBuy', String(onlyForBuy));
    const url = `${this.baseUrl}api/v1/materialmanagement/Vendors__orchSuffix__/GetVendorsWithNames`;
    return this.http.get<ApiResponse<VendorName[]> | VendorName[]>(url, { params });
  }

  download(filter: CommitsFilterDto, columns: string[]): Observable<Blob> {
    const email = this.upnSnapshot();

    const dtoFilter: CommitsFilterDownloadDTO = {
      ...buildBaseCommitDto(filter, email),
      Fields: columns,
      RequestNumbers: splitWith(filter.requestNumbers),
      type: 'XLSX',
    };

    if (filter.sorts) dtoFilter.sorts = filter.sorts;

    applyDateFilter(filter, dtoFilter);
    return this.getUploadCommits(dtoFilter, filter.plants);
  }

  getCommitsForBatchEdit(filter: CommitsFilterDto): Observable<Commit[]> {
    const email = this.upnSnapshot();

    const dtoFilter: CommitsFilterUIModel = {
      ...buildBaseCommitDto(filter, email),
      buyers: splitWith(filter.buyers),
      creators: splitWith(filter.creators),
      inboundDeliveryNumbers: splitWith(filter.inboundDeliveryNumbers, /[,\s]+/),
      includeParentIDs: filter.includeParentIDs,
      requestNumbers: splitWith(filter.rqs),
      vendorCodes: vendorsToUpper(splitWith(filter.vendorCodes)),
    };

    if (filter.CheckLargeData === false) dtoFilter.CheckLargeData = false;

    applyDateFilter(filter, dtoFilter);

    this.store.dispatch(
      new SetDateRangeParameters({ dateFrom: filter.dateFrom, dateTo: filter.dateTo }),
    );

    return this.http.post<Commit[]>(this.commitsAction('GetCommitsForBatchEdit'), dtoFilter, {
      context: requestTenantsContext(filter.plants),
    });
  }

  getUploadCommits(
    payload: CommitsFilterDownloadDTO,
    plants?: readonly string[],
  ): Observable<Blob> {
    return this.http.post(this.commitsAction('GetUploadCommits'), payload, {
      context: requestTenantsContext(plants),
      responseType: 'blob',
    });
  }

  uploadFile(
    fileName: string,
    file: File,
    email: string,
    checkRowCount = true,
  ): Observable<HttpEvent<RequestStatusDto>> {
    const url = `${this.commitsAction('PostUploadCommits')}?Email=${email}&checkrowcount=${checkRowCount}`;
    return this.fileUpload.uploadFormData(url, fileName, file);
  }

  getCommitDocuments(inboundDeliveryNumber: string): Observable<DocumentNameDto[]> {
    return this.http.get<DocumentNameDto[]>(
      this.commitDocumentsAction(inboundDeliveryNumber, 'commitdocuments'),
    );
  }

  updateCommitDocuments(inboundDeliveryNumber: string, data: unknown): Observable<unknown> {
    return this.http.put(
      this.commitDocumentsAction(inboundDeliveryNumber, 'commitdocuments'),
      data,
    );
  }

  uploadCommitDocuments(
    inboundDeliveryNumber: string,
    data: FileData,
  ): Observable<HttpEvent<string>> {
    const formData = new FormData();
    formData.append('Content', data.file);
    formData.append('Description', data.description);

    const req = new HttpRequest(
      'POST',
      this.commitDocumentsAction(inboundDeliveryNumber, 'uploadcommitdocuments'),
      formData,
      { reportProgress: true, responseType: 'text' as const },
    );

    return this.http.request<string>(req);
  }

  downloadCommitDocuments(inboundDeliveryNumber: string, docName: string): Observable<Blob> {
    return this.http.get(
      `${this.commitDocumentsAction(inboundDeliveryNumber, 'downloadcommitdocuments')}/${docName}`,
      { responseType: 'blob' },
    );
  }

  deleteCommitDocuments(inboundDeliveryNumber: string, docName: string): Observable<boolean> {
    return this.http.delete<boolean>(
      `${this.commitDocumentsAction(inboundDeliveryNumber, 'commitdocuments')}/${docName}`,
    );
  }

  // -----------------------
  // Local cached data - TODO find puprose of the functions and variable.
  // -----------------------
  setFilteredData(data: CommitModuleRedirectDTO | null): void {
    this.filteredData = data;
  }

  getFilteredData(): CommitModuleRedirectDTO | null {
    return this.filteredData;
  }

  // -----------------------
  // Default form settings
  // -----------------------
  getDefaultFormSettingFields(formKey: string): Observable<string[]> {
    return this.defaultForms.getFields(this.commitsUrl, formKey);
  }

  getDefaultFormSettings(formKey: string): Observable<FormSetting[]> {
    return this.defaultForms.getFormSettings(this.commitsUrl, formKey);
  }

  getDefaultFormSettingFormKeys(): Observable<string[]> {
    return this.defaultForms.formKeys(this.commitsUrl);
  }

  updateDefaultFormSettings(formKey: string, payload: FormSetting[]): Observable<FormSetting[]> {
    return this.defaultForms.update(this.commitsUrl, formKey, payload);
  }
}
